import { useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  isOrgScopedPathname,
  getOrgIdFromPathname,
  resolveOrgSwitchTargetPath,
} from '../app/Routes/orgRouteUtils';
import type { UserOrganizationDto } from '../api';

type UseOrgScopedRouteSyncOptions = {
  orgs: UserOrganizationDto[];
  selectedOrg?: string;
  setSelectedOrg: (orgId: string) => void;
  startTransition: (callback: () => void) => void;
};

export const useOrgScopedRouteSync = ({
  orgs,
  selectedOrg,
  setSelectedOrg,
  startTransition,
}: UseOrgScopedRouteSyncOptions) => {
  const history = useHistory();
  const location = useLocation();

  const syncOrgFromPath = useCallback(() => {
    if (!isOrgScopedPathname(location.pathname)) return;

    const orgIdFromUrl = getOrgIdFromPathname(location.pathname);
    if (!orgIdFromUrl) return;

    const orgExists = orgs.some(org => org.id === orgIdFromUrl);
    if (orgExists && orgIdFromUrl !== selectedOrg) {
      startTransition(() => setSelectedOrg(orgIdFromUrl));
    }
  }, [location.pathname, orgs, selectedOrg, setSelectedOrg, startTransition]);

  useEffect(() => {
    syncOrgFromPath();
  }, [syncOrgFromPath]);

  const handleOrganizationChange = useCallback(
    (newOrg: string) => {
      startTransition(() => {
        setSelectedOrg(newOrg);

        if (isOrgScopedPathname(location.pathname)) {
          const target = resolveOrgSwitchTargetPath(location.pathname, newOrg);

          if (target) {
            history.replace(target);
          }
        } else {
          const pathSegments = location.pathname.split('/').filter(Boolean);
          if (pathSegments.length >= 2) {
            history.push(`/${pathSegments[0]}`);
          } else {
            history.push(location.pathname);
          }
        }
      });
    },
    [location.pathname, history, setSelectedOrg, startTransition]
  );

  return { handleOrganizationChange };
};
